import { toaster } from "@/components/ui/toaster";
import { RequestTypes } from "@/constants/enums";
import { getFertSlotRequests } from "@/services/fert-slot-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { FuelSlotRequestListResponse } from "./fuel-slot-requests.schema";

interface FetchFuelSlotRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<FuelSlotRequestListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFuelSlotRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchFuelSlotRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getFertSlotRequests({
      ...filters,
      nroCuenta: filters?.nroCuenta?.value,
      tipoSolicitud: RequestTypes.CuposCombustible,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de cupos de combustible.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFiltersHandlerProps) => {
  const fechaEntrega = event?.fechaEntrega
    ? dayjs(event.fechaEntrega).format("YYYY-MM-DD")
    : undefined;

  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const idsDeposito = Array.isArray(event?.idsDeposito)
    ? event.idsDeposito.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const cantidad =
    typeof event?.cantidad === "string" && event.cantidad.trim() !== ""
      ? Number.parseFloat(event.cantidad.replace(",", "."))
      : undefined;

  const nextFilters = {
    fechaEntrega,
    nroCuenta: event?.nroCuenta || undefined,
    procedencia: event?.procedencia || undefined,
    cantidad,
    estados: estados?.length ? estados : undefined,
    idsDeposito: idsDeposito?.length ? idsDeposito : undefined,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFiltersHandlerProps) => {
  setValue("fechaEntrega", undefined);
  setValue("nroCuenta", undefined);
  setValue("procedencia", "");
  setValue("cantidad", "");
  setValue("estados", []);
  setValue("idsDeposito", []);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters({});

  await fetchList({ "P.Pagina": 1 });
};
